import { Check } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  PERSONALITY_DIMENSIONS,
  formatPersonalityTraits,
  parsePersonalityTraits,
} from '@/frontend/pages/Dashboard/DigitalWorkers/constants'
import type { IdentityStepProps } from '@/frontend/pages/Dashboard/DigitalWorkers/new/types'

export default function IdentityStep({
  data,
  errors,
  disabled = false,
  onFieldChange,
}: IdentityStepProps) {
  const selectedTraits = parsePersonalityTraits(data.personality)
  const isSelected = (trait: string) => selectedTraits.includes(trait)

  const toggleTrait = (dimensionValues: string[], trait: string) => {
    const withoutDimension = selectedTraits.filter((value) => !dimensionValues.includes(value))
    const next = isSelected(trait) ? withoutDimension : [...withoutDimension, trait]
    onFieldChange('personality', formatPersonalityTraits(next))
  }

  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <h2 className="text-2xl font-semibold tracking-tight text-foreground">Defina sua personalidade</h2>
        <p className="text-sm text-muted-foreground">Use os campos abaixo para definir os traços de comportamento do agente.</p>
      </div>

      <div className="space-y-4">
        <TooltipProvider delayDuration={150}>
          {PERSONALITY_DIMENSIONS.map((dimension) => {
            const dimensionValues = dimension.options.map((option) => option.value)
            return (
              <div key={dimension.id} className="space-y-2">
                <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2">
                  <span className="text-sm font-semibold text-foreground">{dimension.label}</span>
                  <span className="text-xs text-muted-foreground">{dimension.helper}</span>
                </div>

                <div role="radiogroup" aria-label={dimension.label} className="flex flex-wrap gap-2">
                  {dimension.options.map(({ value, description, icon: Icon }) => {
                    const selected = isSelected(value)
                    return (
                      <Tooltip key={value}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => toggleTrait(dimensionValues, value)}
                            disabled={disabled}
                            className={cn(
                              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40',
                              selected
                                ? 'border-violet-500 bg-violet-500/10 text-violet-700 dark:text-violet-300'
                                : 'border-border bg-card text-foreground hover:border-violet-400/60 hover:-translate-y-[1px] hover:shadow-sm',
                              disabled && 'cursor-not-allowed opacity-60 hover:translate-y-0 hover:shadow-none',
                            )}
                          >
                            <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                              {selected ? (
                                <Check className="h-3.5 w-3.5 animate-check-zoom-in" strokeWidth={3} />
                              ) : (
                                <Icon className="h-3.5 w-3.5" />
                              )}
                            </span>
                            {value}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">{description}</TooltipContent>
                      </Tooltip>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </TooltipProvider>

        {errors.personality && <p className="text-sm text-destructive">{errors.personality}</p>}
      </div>
    </div>
  )
}
